// ReviewsSection.jsx
import React, { useState, useMemo, useEffect } from 'react';

const ReviewsSection = ({ materialReviews, isLoadingReviews, getImageUrl }) => {
  const [activeFilter, setActiveFilter] = useState('most-helpful');
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [lightboxData, setLightboxData] = useState(null);


  // Add this helper function inside your ReviewsSection component
  const getRatingBadgeClass = (rating) => {
    switch (rating) {
      case 1:
        return 'rating-badge-red';
      case 2:
        return 'rating-badge-yellow';
      case 3:
        return 'rating-badge-orange';
      case 4:
      case 5:
        return 'rating-badge-green';
      default:
        return '';
    }
  };

  const [visibleCount, setVisibleCount] = useState(3);

  // Calculate rating statistics
  const ratingStats = useMemo(() => {
    if (materialReviews.length === 0) {
      return { average: 0, total: 0, distribution: { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 } };
    }

    const distribution = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    let sum = 0;

    materialReviews.forEach(review => {
      distribution[review.rating] = (distribution[review.rating] || 0) + 1;
      sum += review.rating;
    });

    return {
      average: (sum / materialReviews.length).toFixed(1),
      total: materialReviews.length,
      distribution
    };
  }, [materialReviews]);

  // Filter and sort reviews
  const sortedReviews = useMemo(() => {
    let sorted = [...materialReviews];

    switch (activeFilter) {
      case 'most-recent':
        sorted.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        break;
      case 'positive-first':
        sorted.sort((a, b) => b.rating - a.rating);
        break;
      case 'negative-first':
        sorted.sort((a, b) => a.rating - b.rating);
        break;
      case 'most-helpful':
      default:
        break;
    }

    return sorted;
  }, [materialReviews, activeFilter]);

  const filterOptions = [
    { value: 'most-helpful', label: 'Most Helpful' },
    { value: 'most-recent', label: 'Most Recent' },
    { value: 'positive-first', label: 'Positive First' },
    { value: 'negative-first', label: 'Negative First' }
  ];

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getRatingBarClass = (star) => {
    if (star === 1) return 'very-low';
    if (star === 2) return 'low';
    return '';
  };

  // Lightbox functions
  const openLightbox = (review, imageIndex) => {
    setLightboxData({
      review,
      currentImageIndex: imageIndex
    });
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setLightboxData(null);
    document.body.style.overflow = 'auto';
  };

  const nextImage = () => {
    if (!lightboxData || !lightboxData.review.images) return;
    const newIndex = (lightboxData.currentImageIndex + 1) % lightboxData.review.images.length;
    setLightboxData({
      ...lightboxData,
      currentImageIndex: newIndex
    });
  };

  const prevImage = () => {
    if (!lightboxData || !lightboxData.review.images) return;
    const newIndex = (lightboxData.currentImageIndex - 1 + lightboxData.review.images.length) % lightboxData.review.images.length;
    setLightboxData({
      ...lightboxData,
      currentImageIndex: newIndex
    });
  };

  const handleKeyDown = (e) => {
    if (!lightboxData) return;
    if (e.key === 'Escape') closeLightbox();
    if (e.key === 'ArrowRight') nextImage();
    if (e.key === 'ArrowLeft') prevImage();
  };

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [lightboxData]);

  if (isLoadingReviews) {
    return (
      <div className="tab-pane-reviews">
        <div className="reviews-loading">
          <i className="ph ph-spinner" style={{ animation: 'spin 1s linear infinite' }}></i>
          <span>Loading reviews...</span>
        </div>
      </div>
    );
  }

  if (materialReviews.length === 0) {
    return (
      <div className="tab-pane-reviews">
        <div className="no-reviews">
          <i className="ph ph-star" style={{ fontSize: '48px', color: '#d4d5d9' }}></i>
          <p>No reviews yet for this material.</p>
          <span className="no-reviews-subtext">Be the first to review this product!</span>
        </div>
      </div>
    );
  }

  return (
    <div className="tab-pane-reviews">
      {/* Header with Filter */}
      <div className="reviews-header">
        <h6 className="reviews-title">
          <i className="ph ph-star"></i>
          Ratings & Reviews
        </h6>
        <div className="filter-dropdown">
          <button
            className="filter-button"
            onClick={() => setShowFilterMenu(!showFilterMenu)}
          >
            <span>{filterOptions.find(opt => opt.value === activeFilter)?.label}</span>
            <i className="ph ph-caret-down"></i>
          </button>
          {showFilterMenu && (
            <div className="filter-menu">
              {filterOptions.map(option => (
                <div
                  key={option.value}
                  className={`filter-option ${activeFilter === option.value ? 'active' : ''}`}
                  onClick={() => {
                    setActiveFilter(option.value);
                    setShowFilterMenu(false);
                  }}
                >
                  {option.label}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Rating Summary Section */}
      <div className="rating-summary">
        <div className="rating-overview">
          <div className="rating-score">
            {ratingStats.average}
            <i className="ph-fill ph-star"></i>
          </div>
          <div className="rating-count">
            {ratingStats.total} Rating{ratingStats.total !== 1 ? 's' : ''} & {materialReviews.length} Review{materialReviews.length !== 1 ? 's' : ''}
          </div>
        </div>

        <div className="rating-distribution">
          {[5, 4, 3, 2, 1].map(star => {
            const count = ratingStats.distribution[star] || 0;
            const percentage = ratingStats.total > 0 ? (count / ratingStats.total) * 100 : 0;

            return (
              <div key={star} className="rating-bar-item">
                <div className="rating-bar-label">
                  {star}
                  <i className="ph-fill ph-star"></i>
                </div>
                <div className="rating-bar-container">
                  <div
                    className={`rating-bar-fill ${getRatingBarClass(star)}`}
                    style={{ width: `${percentage}%` }}
                  ></div>
                </div>
                <div className="rating-bar-count">{count}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Reviews List */}
      <div className="review-cards-list">
        {sortedReviews.slice(0, visibleCount).map(review => (
          <div key={review.id} className="review-card">
            <div className="review-header">
              <div className={`review-rating-badge ${getRatingBadgeClass(review.rating)}`}>
                {review.rating}
                <i className="ph-fill ph-star"></i>
              </div>
              <span className="review-user">{review.user_name || 'Anonymous'}</span>
              <span className="review-verified">
                <i className="ph-fill ph-check-circle"></i>
                Certified Buyer
              </span>
              <span className="review-date">
                {formatDate(review.created_at)}
              </span>
            </div>

            <div className="review-body">
              {review.material_name && (
                <div className="review-material">{review.material_name}</div>
              )}
              <div className="review-description">{review.description}</div>
              {review.images && review.images.length > 0 && (
                <div className="review-images">
                  {review.images.map((img, idx) => (
                    <img
                      src={getImageUrl(img.image_url)}
                      alt={`Review ${idx + 1}`}
                      key={idx}
                      className="review-image"
                      onClick={() => openLightbox(review, idx)}
                    />
                  ))}
                </div>
              )}
            </div>
          
          </div>
        ))}
          {materialReviews.length > 3 && (
              <div className="reviews-more-btn-container">
                <button
                  className="reviews-more-btn"
                  onClick={() => {
                    if (visibleCount >= sortedReviews.length) {
                      setVisibleCount(3);
                    } else {
                      setVisibleCount((prev) => Math.min(prev + 2, sortedReviews.length));
                    }
                  }}
                >
                  {visibleCount >= sortedReviews.length ? 'Show Less' : 'Show More'}
                </button>
              </div>
            )}
      </div>
      

      {/* Flipkart-style Image Lightbox Modal */}
      {lightboxData && (
        <div className="lightbox-overlay" onClick={closeLightbox}>
          <div className="lightbox-container" onClick={(e) => e.stopPropagation()}>
            {/* Close Button */}
            <button className="lightbox-close" onClick={closeLightbox}>
              <i className="ph ph-x"></i>
            </button>

            {/* Left Side - Image Section */}
            <div className="lightbox-left">
              <div className="lightbox-image-wrapper">
                <img
                  src={getImageUrl(lightboxData.review.images[lightboxData.currentImageIndex].image_url)}
                  alt="Review"
                  className="lightbox-main-image"
                />

                {/* Navigation Arrows */}
                {lightboxData.review.images.length > 1 && (
                  <>
                    <button className="lightbox-arrow lightbox-arrow-left" onClick={prevImage}>
                      <i className="ph ph-caret-left"></i>
                    </button>
                    <button className="lightbox-arrow lightbox-arrow-right" onClick={nextImage}>
                      <i className="ph ph-caret-right"></i>
                    </button>
                  </>
                )}
              </div>

              {/* Thumbnails */}
              {lightboxData.review.images.length > 1 && (
                <div className="lightbox-thumbnails-bottom">
                  {lightboxData.review.images.map((img, idx) => (
                    <img
                      key={idx}
                      src={getImageUrl(img.image_url)}
                      alt={`Thumbnail ${idx + 1}`}
                      className={`lightbox-thumb ${idx === lightboxData.currentImageIndex ? 'active' : ''}`}
                      onClick={() => setLightboxData({ ...lightboxData, currentImageIndex: idx })}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Right Side - Review Details */}
            <div className="lightbox-right">
              <div className="lightbox-review-header">
                <div className={`lightbox-rating-badge ${getRatingBadgeClass(lightboxData.review.rating)}`}>
                  {lightboxData.review.rating}
                  <i className="ph-fill ph-star"></i>
                </div>
                <h3 className="lightbox-review-title">
                  {lightboxData.review.rating === 5 ? 'Perfect product!' :
                    lightboxData.review.rating === 4 ? 'Great product' :
                      lightboxData.review.rating === 3 ? 'Good product' :
                        lightboxData.review.rating === 2 ? 'Slightly disappointed' :
                          'Not satisfied'}
                </h3>
              </div>

              <div className="lightbox-review-description">
                {lightboxData.review.description}
              </div>

              <div className="lightbox-review-meta">
                <div className="lightbox-meta-item">
                  <span className="lightbox-meta-label">{lightboxData.review.user_name || 'Anonymous'}</span>
                  <span className="lightbox-verified-badge">
                    <i className="ph-fill ph-check-circle"></i>
                    Certified Buyer
                  </span>
                </div>
                {lightboxData.review.material_name && (
                  <div className="lightbox-meta-item">
                    <span className="lightbox-meta-value">{lightboxData.review.material_name}</span>
                  </div>
                )}
                <div className="lightbox-meta-item">
                  <span className="lightbox-meta-date">{formatDate(lightboxData.review.created_at)}</span>
                </div>
              </div>

              {/* <div className="lightbox-helpful-section">
                <span className="lightbox-helpful-text">Was this review helpful?</span>
                <div className="lightbox-helpful-buttons">
                  <button className="lightbox-helpful-btn">
                    <i className="ph ph-thumbs-up"></i>
                    <span>1</span>
                  </button>
                  <button className="lightbox-helpful-btn">
                    <i className="ph ph-thumbs-down"></i>
                    <span>0</span>
                  </button>
                </div>
              </div> */}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReviewsSection;